import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
    // useHistory,
    useLocation
} from 'react-router-dom'

import {appSetModalContent} from "../../../../redux/app/appActions";

import SettingsForm from "../settings/SettingsForm";

import useFeature from "../../../../hooks/useFeature";
import useDataSource from "../../../../hooks/useDataSource";

import * as DataUtilities from "../../../../utilities/DataUtilities";

import {
	formatDate,
	formatDuration,
	formatDaysRemaining,
	formatNumber,
	formatStyle,
	formatText,
	formatSuffix, formatPrefix, formatIcon
} from "../../../../utilities/FormatUtilities";

import {
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import {
	LinearProgress,
	styled,
	Typography,
	Button,
	CardHeader,
	CardContent,
	Card,
	Tooltip, Avatar,
} from "@mui/material";
import ClearFilterBtn from "../../../application/ClearFilterBtn";
import {getInsightDateVariance, getInsightNumberVariance} from "../../../../utilities/InsightUtilities";
import {findComponent} from "../../../../redux/page/pageActions";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import {dataAddFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";
import {push} from "connected-react-router";

const PREFIX = 'MetricCard';

const classes = {
    root: `${PREFIX}-root`,
    noLayout: `${PREFIX}-noLayout`,
    pointer: `${PREFIX}-pointer`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`,
	circleContainer: `${PREFIX}-circleContainer`,
	iconContainer: `${PREFIX}-iconContainer`,
	icon: `${PREFIX}-icon`,
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: '100%'
    },

    [`& .${classes.noLayout}`]: {
        width: '100%',
        height: '100%',
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        margin: '0px',
        padding: '5px'
    },

    [`& .${classes.pointer}`]: {
        cursor: 'pointer'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none'
    },

	[`& .${classes.circleContainer}`]: {
		width: theme.spacing(8),
		height: theme.spacing(8),
		borderRadius: '50%',
		display: 'block',
		position: 'absolute',
		left: '50%',
		top: '50%',
		margin: '-35px 0 0 -35px',
		color: 'black'
		// backgroundColor: '#00e676'
	},

	[`& .${classes.iconContainer}`]: {
		height: '50%',
		width: '100%',
		textAlign: 'center'
	},

	[`& .${classes.icon}`]: {
		fontSize: '24px',
		verticalAlign: 'middle',
		// marginTop: '10px'
		height: '100%'
	}
}));

export default function MetricCard({id, configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
	const pageID = useSelector(state => state.app.pageID);
    const dataDate = useSelector(state => state.data.dataDate);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [value, setValue] = useState('--'); // TODO: Can we set this differently, i.e. from config?
	const [icon, setIcon] = useState(null);
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
    const [layout, setLayout] = useState(compConfig.element?.cardLayout ? compConfig.element?.cardLayout : false);
    const [insight, setInsight] = useState(null);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [insightContentStyle, setInsightContentStyle] = useState({});
	const [isButton, setIsButton] = useState(compConfig.element?.route !== undefined ? (compConfig.element.route !== "" ? true : false) : false);
	const [buttonRoute, setButtonRoute] = useState(compConfig.element?.route ? compConfig.element?.route : '#');

	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const dataSourceType = compConfig.dataSource?.type ? (compConfig.dataSource.type.toLowerCase() === 'static' ? 'static' : 'dynamic' ) : 'dynamic';
	const dataSourceStaticValue = compConfig.dataSource?.type ? (compConfig.dataSource.type.toLowerCase() === 'static' ? compConfig.dataSource.value : '' ) : '';
	const dataSourceStaticIcon = compConfig.dataSource?.type ? (compConfig.dataSource.type.toLowerCase() === 'static' ? compConfig.element.icon : '' ) : '';
	const field = compConfig.dataSource?.fields?.field ? compConfig.dataSource?.fields?.field : '';
	const styles = compConfig?.element?.styles;
	const condition = compConfig.dataSource?.condition ? compConfig.dataSource?.condition : null;
	const insightConfig = compConfig.dataSource?.insight ? compConfig.dataSource?.insight : {enabled: false};

	const [insightTooltip, setInsightTooltip] = useState(insightConfig?.effect?.metric?.tooltip !== undefined ? insightConfig.effect.metric.tooltip : '');

	let dataSource = useDataSource(dataSourceName);

    // When using Args i.e. :list
    let list =  useLocation().pathname;
    list = /.*\/([^?]+)/.exec(list)[1];
    const feature = useFeature(list);
    const featureTarget = feature ? feature.options?.featureOptions?.target?.name : '';

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Titles
	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

    // Data
    useEffect(() => {

        if (dataSourceType === 'dynamic' && dataSource.data && dataSource.fetched) {

            let data =[];
            if (condition) {
                let conditionValue;
                switch (condition.conditionValue.toLowerCase()) {
                    case 'datadate':
                        conditionValue = dataDate;
                        break;
                    default:
                        conditionValue = condition.conditionValue
                        break;
                }
                switch (condition.conditionType.toLowerCase()) {
                    case 'pathname':
                        conditionValue = featureTarget;
                        break;
                    default:
                        conditionValue = condition.conditionValue
                        break;
                }
                const newData = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, conditionValue, dataSource.data, null);
                // If condition found, push, otherwise, keep null/empty
                if (newData !== undefined) {
                    data.push(newData);
                }
            } else {
                data = dataSource.data;
            }

            if (data.length === 1) {

                let insightValue = {};
                let insightFormatters = [];
                let formattedInsight;
                if (insightConfig.enabled) {

                    // Filter DataSet for Insight
                    let conditionValue, baselineData;
                    if (insightConfig?.condition !== undefined) {
                        switch (insightConfig.condition.conditionValue.toLowerCase()) {
                            default:
                                conditionValue = insightConfig.condition.conditionValue
                                break;
                        }
                        switch (insightConfig.condition.conditionType.toLowerCase()) {
                            default:
                                conditionValue = insightConfig.condition.conditionValue
                                break;
                        }
                        baselineData = DataUtilities.filterDataSet(insightConfig.condition.conditionType, insightConfig.condition.conditionOperator, insightConfig.condition.conditionField, conditionValue, dataSource.data, null);
                    } else {
                        baselineData = data[0];
                    }

                    // Insight Type
                    if (insightConfig.type.toLowerCase() === 'datevariance') {
                        insightValue = getInsightDateVariance(data[0][field.name], baselineData, insightConfig);
                    } else if (insightConfig.type.toLowerCase() === 'numbervariance') {
                        insightValue = getInsightNumberVariance(data[0][field.name], baselineData, insightConfig);
                    }

                    // Format the Insights Metric Value
                    if (insightValue?.formatActualAndInsight !== undefined) {
                        if (insightValue.formatActualAndInsight) {
                            insightFormatters = insightConfig.effect.metric.formatters;
                            formattedInsight = insightValue.metric;
                            for (const formatter of insightFormatters) {
                                if (formatter.type.toLowerCase() === 'prefix') {
                                    formattedInsight = formatPrefix(formattedInsight, formatter.config);
                                } else if (formatter.type.toLowerCase() === 'style') {
                                    formattedInsight = formatStyle(formattedInsight, formatter.config);
                                } else if (formatter.type.toLowerCase() === 'text') {
                                    formattedInsight = formatText(formattedInsight, formatter.config);
                                } else if (formatter.type.toLowerCase() === 'suffix') {
                                    formattedInsight = formatSuffix(formattedInsight, formatter.config);
                                }
                            }
                        }
                    }
                }

                // Format Actual Value
                let formatters = [];
                if (insightValue?.formatActualAndInsight !== undefined) {
                    if (insightValue.formatActualAndInsight) {
                        formatters = insightConfig.effect.actual.formatters;
                    } else {
                        formatters = field?.formatters.length > 0 ? field.formatters : getFieldFormatters(dataSource, field.name);
                    }
                } else {
                    formatters = field?.formatters.length > 0 ? field.formatters : getFieldFormatters(dataSource, field.name);
                }

                let formattedValue = data[0][field.name];
                // console.log('metriccard: Before formatters: ', formattedValue);
                for (const formatter of formatters) {
                    if (formatter.type.toLowerCase() === 'date') {
                        formattedValue = formatDate(formattedValue, formatter.config);
                    } else if (formatter.type.toLowerCase() === 'duration') {
                        formattedValue = formatDuration(data[0].min, data[0].max, formatter.config);
                    } else if (formatter.type.toLowerCase() === 'daysremaining') {
                        formattedValue = formatDaysRemaining(data[0].daysremaining, formatter.config);
                    } else if (formatter.type.toLowerCase() === 'number') {
                        formattedValue = formatNumber(formattedValue, formatter.config);
                    } else if (formatter.type.toLowerCase() === 'style') {
                        formattedValue = formatStyle(formattedValue, formatter.config, data[0][field.name], formatter.condition, false);
                    } else if (formatter.type.toLowerCase() === 'text') {
                        formattedValue = formatText(formattedValue, formatter.config);
                    } else if (formatter.type.toLowerCase() === 'suffix') {
                        formattedValue = formatSuffix(formattedValue, formatter.config);
                    } else {

                    }
                }
                // console.log('metriccard: After formatters: ', formattedValue);
                setValue(formattedValue);
                setInsight(formattedInsight ? formattedInsight : null);
            } else {
                setValue("--"); // TODO: Can we set this differently, i.e. from config?
            }
        } else if (dataSourceType === 'static') {
			setValue(dataSourceStaticValue);
			setIcon(formatIcon(dataSourceStaticIcon));
		} else {
			// console.log('{0}'.format('--> metriccard: useEffect() on dataSource: Else condition'));
			// console.log(dataSource);
			setValue("--"); // TODO: Can we set this differently, i.e. from config?
		}
    }, [dataSource.data, dataSourceType, t]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'MetricCard Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'MetricCard'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

	const handleClick = () => {
		if (isButton === true) {
			dispatch(push('{0}'.format(buttonRoute)));
		}
	}

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {
			...styles?.cardContent
			, height: 'calc(100% - 49px)'
			, overflow: 'hidden'
			, padding: '0px'
			, display: 'flex'
			, flexDirection: 'column'
			, justifyContent: 'center'
		});
		const newInsightContentStyle = Object.assign({}, {...insightConfig?.effect?.metric?.styles?.insightContent});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
		setInsightContentStyle(newInsightContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {layout &&
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{variant: 'subtitle2', color: 'textPrimary'}}
                            title={t(title)}
                            subheaderTypographyProps={{variant: 'body2', color: 'textSecondary'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <Typography variant="h5" gutterBottom align="center" style={{}}>
                        {value}
                    </Typography>
					{icon &&
						<Typography variant="h5" gutterBottom align="center" style={{position: 'relative', top: '0px', width: '100%', height: '40%', marginTop: '3px'}}>
							<div style={{display: 'inline-flex', alignItems: 'center'}}>
								<Tooltip title={value} arrow placement="bottom">
									<Avatar className={classes.circleContainer} style={{cursor: isButton ? 'pointer' : 'default'}} onClick={() =>{ handleClick() } }>
										<div className={classes.iconContainer} style={{paddingTop: '20px'}}>
											<span className={classes.icon}>{icon}</span>
										</div>
									</Avatar>
								</Tooltip>
							</div>
						</Typography>
					}
                    {insight &&
                        <Tooltip title={insightTooltip} arrow placement="top">
                            <div style={insightContentStyle}>
                                {insight}
                            </div>
                        </Tooltip>
                    }
                </CardContent>
            </Card>
            }
            {!layout &&
            <div className={clsx(classes.noLayout)}>
                {ClearFilterBtn(id, false)}
                <Typography variant="subtitle1" display="block" gutterBottom align="center">
                    {t(title)}
                </Typography>
                <Typography variant="h6" gutterBottom align="center" style={{cursor: isButton ? 'pointer' : 'default'}}  onClick={() =>{ handleClick() } }>
                    {value}
                </Typography>
                {insight &&
                    <Tooltip title={insightTooltip} arrow placement="top">
                        <div style={insightContentStyle}>
                            {insight}
                        </div>
                    </Tooltip>
                }
                <Typography variant="caption" display="block" gutterBottom align="center">
                    {t(subtitle)}
                </Typography>
            </div>
            }
        </Root>
    );
}
